import { useMutation, useQuery, useQueryClient, type QueryKey, type UseQueryResult } from "@tanstack/react-query";

import { apiRequest } from "../../../common/api/apiClient.js";
import type {
  AttendeeStatus,
  CalendarDTO,
  CalendarSharesDTO,
  CreateEventRequest,
  CreateShareRequest,
  EventDTO,
  ShareDTO,
  UpdateEventRequest,
} from "../../../common/api/types.js";

export const calendarsQueryKey = ["calendar", "calendars"] as const;
export const eventsQueryKey = (from: string, to: string) => ["calendar", "events", from, to] as const;
export const calendarSharesQueryKey = (id: string) => ["calendar", "shares", id] as const;

export function useCalendars(): UseQueryResult<CalendarDTO[], Error> {
  return useQuery({
    queryKey: calendarsQueryKey,
    queryFn: () => apiRequest<CalendarDTO[]>("/calendar/calendars"),
  });
}

export function useEvents(from: string, to: string): UseQueryResult<EventDTO[], Error> {
  return useQuery({
    queryKey: eventsQueryKey(from, to),
    queryFn: () =>
      apiRequest<EventDTO[]>(
        `/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
  });
}

export function useCalendarShares(calendarId: string | null): UseQueryResult<CalendarSharesDTO, Error> {
  return useQuery({
    queryKey: calendarSharesQueryKey(calendarId ?? ""),
    queryFn: () => apiRequest<CalendarSharesDTO>(`/calendar/calendars/${calendarId as string}/shares`),
    enabled: calendarId !== null,
  });
}

export function useCreateEvent() {
  const client = useQueryClient();
  return useMutation<EventDTO, Error, CreateEventRequest>({
    mutationFn: (body) => apiRequest<EventDTO>("/calendar/events", { method: "POST", body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["calendar", "events"] });
    },
  });
}

export function useUpdateEvent() {
  const client = useQueryClient();
  return useMutation<EventDTO, Error, { eventId: string; body: UpdateEventRequest }, Array<[QueryKey, EventDTO[] | undefined]>>({
    mutationFn: ({ eventId, body }) =>
      apiRequest<EventDTO>(`/calendar/events/${eventId}`, { method: "PUT", body }),
    onMutate: async ({ eventId, body }) => {
      await client.cancelQueries({ queryKey: ["calendar", "events"] });
      const previous = client.getQueriesData<EventDTO[]>({ queryKey: ["calendar", "events"] });
      client.setQueriesData<EventDTO[]>({ queryKey: ["calendar", "events"] }, (current) =>
        current?.map((event) => event.id === eventId ? { ...event, ...body } : event),
      );
      return previous;
    },
    onError: (_error, _variables, previous) => {
      previous?.forEach(([key, events]) => client.setQueryData(key, events));
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ["calendar", "events"] });
    },
  });
}

export function useDeleteEvent() {
  const client = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (eventId) => apiRequest<void>(`/calendar/events/${eventId}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["calendar", "events"] });
    },
  });
}

/** Zu- oder Absage auf eine Einladung. Das darf jeder Teilnehmer, auch ohne Schreibrecht. */
export function useRespondToEvent() {
  const client = useQueryClient();
  return useMutation<EventDTO, Error, { eventId: string; status: AttendeeStatus }>({
    mutationFn: ({ eventId, status }) =>
      apiRequest<EventDTO>(`/calendar/events/${eventId}/response`, {
        method: "POST",
        body: { status },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["calendar", "events"] });
    },
  });
}

export function useShareCalendar(calendarId: string | null) {
  const client = useQueryClient();
  return useMutation<ShareDTO, Error, CreateShareRequest>({
    mutationFn: (body) =>
      apiRequest<ShareDTO>(`/calendar/calendars/${calendarId as string}/shares`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: calendarSharesQueryKey(calendarId ?? "") });
      void client.invalidateQueries({ queryKey: calendarsQueryKey });
    },
  });
}

export function useRevokeCalendarShare(calendarId: string | null) {
  const client = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (shareId) =>
      apiRequest<void>(`/calendar/calendars/${calendarId as string}/shares/${shareId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: calendarSharesQueryKey(calendarId ?? "") });
    },
  });
}
